import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface SearchHighlightProps {
  /** Texto completo a ser exibido. */
  text: string;
  /** Termo de busca a destacar (case-insensitive). */
  query?: string;
  /** Classes CSS adicionais para o <mark>. */
  markClassName?: string;
}

/**
 * Renderiza `text` destacando todas as ocorrências de `query`.
 *
 * Uso:
 * ```tsx
 * <SearchHighlight text={item.title} query={searchTerm} />
 * ```
 */
export function SearchHighlight({
  text,
  query,
  markClassName,
}: SearchHighlightProps): ReactNode {
  if (!query || !text) return <>{text}</>;

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);

  if (parts.length === 1) return <>{text}</>;

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            className={cn(
              'rounded-sm bg-yellow-200 px-0.5 text-inherit [font-family:inherit] dark:bg-yellow-700/60',
              markClassName,
            )}
          >
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}
